/**
 * The requests inbox — FR-C5, T-2.7. A list since Wave 15C (C14 §10.6): a row
 * opens the request in a dialog, where it is accepted into a case or declined.
 *
 * A request is **not a case**. It appears here and in no other queue, and this
 * screen is where somebody turns one into work or says no.
 *
 * The one interesting decision on it: **a request from the public form names an
 * entity with no client record**, and the API will not create a client from
 * unverified form input. So accepting asks for a client first, showing what the
 * requester typed beside the picker, rather than accepting and failing. The
 * rule lives on the server; `requestNeedsClient` is the screen's half of it.
 *
 * **A decline carries its reason.** Required by the API and by a CHECK on the
 * row, and asked for in a confirmation: the client is told, and "Declined" on
 * its own is what generates the phone call.
 *
 * **C27 (UAT reports #13, #19).** The client is marked required, *Choose a
 * client…* is a placeholder rather than a choice, and *Accept* pressed without
 * one says so under the field.
 */

import { Button, ConfirmDialog, Dialog, EmptyState, Select, formatDateTime } from '@fiveox/ui'
import { useMemo, useState } from 'react'

import {
  DialogActions,
  FORM_GRID,
  FormRow,
  type ListColumn,
  ListTable,
  ListToolbar,
  useListLoading,
  useListTable,
} from '../../components/page'
import { ApiError } from '../../lib/api'
import { type ClientRecord } from '../../lib/api/clients'
import { type ServiceRequest, caseFlows, entityLine, requestNeedsClient } from '../../lib/api/cases'

const SOURCE_LABELS: Record<ServiceRequest['source'], string> = {
  site: 'Your website',
  portal: 'The client portal',
}

function RequestDialog({
  row,
  clientList,
  onClose,
  onDone,
}: {
  row: ServiceRequest
  clientList: ClientRecord[]
  onClose: () => void
  onDone: () => void
}) {
  const [client, setClient] = useState(row.client_id ?? '')
  const [declining, setDeclining] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [tried, setTried] = useState(false)
  const needsClient = requestNeedsClient(row)
  const entity = entityLine(row)

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      onDone()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Dialog
        open={!declining}
        onClose={onClose}
        title={row.service_name ?? 'A service request'}
        error={error}
        width="lg"
      >
        <div className={FORM_GRID}>
          <FormRow label="From">
            <p className="text-foreground text-sm sm:pt-3">
              {row.contact_name}
              <span className="text-muted-foreground block text-xs">
                {[row.contact_email, row.contact_mobile, SOURCE_LABELS[row.source]]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </p>
          </FormRow>

          {row.message && (
            <FormRow label="They wrote">
              <p className="text-foreground text-sm whitespace-pre-line sm:pt-3">{row.message}</p>
            </FormRow>
          )}

          {entity && (
            <FormRow label="The entity">
              {/* What they typed, never turned into a client record by this
                  app. Shown so the person accepting can match it to one — or
                  create it in Clients first, with its own validation. */}
              <p className="text-foreground text-sm sm:pt-3">{entity}</p>
            </FormRow>
          )}

          <FormRow label="Open the case for" htmlFor="request-client" required>
            <Select
              id="request-client"
              value={client}
              onChange={(e) => setClient(e.target.value)}
              required
              aria-required="true"
              error={tried && !client ? 'Choose the client the case is for.' : null}
              hint={
                needsClient
                  ? 'This request came from somebody who is not a client yet. Create the client record in Clients first, or match it to one here.'
                  : undefined
              }
            >
              <option value="" disabled hidden>
                Choose a client…
              </option>
              {clientList.map((record) => (
                <option key={record.id} value={record.id}>
                  {record.display_name || record.legal_name}
                </option>
              ))}
            </Select>
          </FormRow>
        </div>

        <DialogActions>
          <Button variant="ghost" onClick={() => setDeclining(true)} disabled={busy}>
            Decline
          </Button>
          <Button
            disabled={busy}
            loading={busy}
            onClick={() => {
              setTried(true)
              if (client) void act(() => caseFlows.acceptRequest(row.id, { client_id: client }))
            }}
          >
            Accept and open a case
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={declining}
        onClose={() => setDeclining(false)}
        title="Decline this request"
        tone="danger"
        confirmLabel="Decline"
        cancelLabel="Back"
        busy={busy}
        error={error}
        reason={{
          label: 'Why is the firm declining?',
          hint: 'Your client is told. “Declined” on its own is what generates the phone call.',
          required: 'Say why — your client is told, and acts on it.',
        }}
        onConfirm={({ reason }) => void act(() => caseFlows.declineRequest(row.id, reason))}
      >
        <p>
          {row.contact_name} asked for {row.service_name ?? 'a service'}. No case is opened.
        </p>
      </ConfirmDialog>
    </>
  )
}

export function RequestList({
  rows,
  clientList,
  onDone,
}: {
  rows: ServiceRequest[]
  clientList: ClientRecord[]
  onDone: () => void
}) {
  const loading = useListLoading()
  const [open, setOpen] = useState<ServiceRequest | null>(null)

  const columns = useMemo<ListColumn<ServiceRequest>[]>(
    () => [
      {
        key: 'service',
        label: 'Service',
        width: 200,
        required: true,
        phone: true,
        render: (row) => (
          <span className="text-foreground font-medium">{row.service_name ?? 'A service'}</span>
        ),
        exportValue: (row) => row.service_name ?? '',
      },
      {
        key: 'from',
        label: 'From',
        width: 200,
        phone: true,
        sortField: 'contact_name',
        render: (row) => (
          <div className="min-w-0">
            <p className="text-foreground truncate">{row.contact_name}</p>
            <p className="text-muted-foreground truncate text-xs">
              {row.contact_email ?? row.contact_mobile ?? ''}
            </p>
          </div>
        ),
      },
      {
        key: 'client',
        label: 'Client',
        width: 140,
        render: (row) =>
          requestNeedsClient(row) ? (
            <span className="text-muted-foreground">Not a client yet</span>
          ) : (
            <span className="text-foreground">On file</span>
          ),
      },
      {
        key: 'source',
        label: 'Came from',
        width: 150,
        sortField: 'source',
        render: (row) => <span className="text-muted-foreground">{SOURCE_LABELS[row.source]}</span>,
      },
      {
        key: 'received_at',
        label: 'Received',
        width: 150,
        sortField: 'received_at',
        render: (row) => (
          <span className="text-muted-foreground whitespace-nowrap">
            {row.received_at ? formatDateTime(row.received_at) : '—'}
          </span>
        ),
      },
    ],
    [],
  )

  const list = useListTable(rows, {
    columns,
    search: (row) => [row.service_name, row.contact_name, row.contact_email, row.contact_mobile],
    filters: [
      {
        key: 'source',
        label: 'Came from',
        allLabel: 'Anywhere',
        options: [
          { value: 'site', label: SOURCE_LABELS.site },
          { value: 'portal', label: SOURCE_LABELS.portal },
        ],
        match: (row, value) => row.source === value,
      },
    ],
    initialSort: { field: 'received_at', dir: 'desc' },
  })

  if (!loading && list.isEmpty) {
    return (
      <EmptyState
        title="No requests waiting"
        description="Requests arrive from your website’s service pages and from the client portal. Accept one and it becomes a case."
      />
    )
  }

  return (
    <div className="space-y-4">
      <ListToolbar list={list} placeholder="Search by service or name" />
      <ListTable
        list={list}
        rowKey={(row) => row.id}
        onRowClick={setOpen}
        trailing={{
          header: <span className="sr-only">Actions</span>,
          width: 100,
          cell: (row) => (
            <Button size="sm" variant="outline" onClick={() => setOpen(row)}>
              Review
            </Button>
          ),
        }}
      />
      {open && (
        <RequestDialog
          row={open}
          clientList={clientList}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null)
            onDone()
          }}
        />
      )}
    </div>
  )
}
